import React, { useRef, useEffect } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import { BrianSimmonsImg, DummyProfileImg, DummyProfileImg1, rapidoSvg, dashboardats, aiPowered, click, calender, offer, analysis, dashboardPreview, dashboardDemo } from '../assets/images';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// FAQ data for Pricing page
const faqData = [
    {
        question: "Do I need to buy all modules at once?",
        answer: "Nope! You can start with just what you need and add others when you're ready. We'll help you design the best-fit setup.",
    },
    {
        question: "Can I switch plans later?",
        answer: "Absolutely. We'll make sure the transition is smooth as your team grows or restructures.",
    },
    {
        question: "Do you help with onboarding?",
        answer: "Yes! Our team will guide you through every step to ensure a smooth onboarding process.",
    },
    {
        question: "Does Symplora integrate with my current tools?",
        answer: "Yes, Symplora integrates with a wide range of popular HR and productivity tools.",
    },
    {
        question: "How fast can we go live?",
        answer: "Implementation timelines vary, but many teams are up and running within a few weeks.",
    },
];

function FaqSection({ faqRefs }) {
    const [openIndex, setOpenIndex] = React.useState(0);

    return (
        <section className="container faq-section">
            <h2 className="faq-title text-heading-2" ref={faqRefs[0]}>
                Frequently Asked <span className="highlight">Questions</span>
            </h2>
            <div className="faq-list" ref={faqRefs[1]}>
                {faqData.map((item, idx) => (
                    <div
                        className={`faq-item${openIndex === idx ? ' open' : ''}`}
                        key={item.question}
                        onClick={() => setOpenIndex(openIndex === idx ? -1 : idx)}
                    >
                        <div className="faq-question-row">
                            <span className="faq-question text-subheading">{item.question}</span>
                            <span className="faq-icon"><svg style={{ transform: openIndex === idx ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease-in-out' }} xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
                                < rect x="24.875" y="25.1423" width="23.75" height="23.75" rx="11.875" transform="rotate(-180 24.875 25.1423)" stroke="#0C0B0B" stroke-width="1.25" />
                                <path d="M9.96191 11.6907L9.99219 11.6438L12.8994 8.73755C12.9409 8.6961 13.0016 8.68568 13.0527 8.7063L13.0996 8.73755L16.0068 11.6438C16.0347 11.6716 16.0479 11.7077 16.0479 11.7444C16.0479 11.7628 16.045 11.781 16.0381 11.7981L16.0068 11.845C15.9653 11.8863 15.9046 11.8959 15.8535 11.8752L15.8066 11.845L13.1416 9.17993L13.1416 17.6975C13.1416 17.7756 13.0781 17.839 13 17.8391C12.9218 17.8391 12.8584 17.7757 12.8584 17.6975L12.8584 9.17993L12.3242 9.71313L10.1934 11.845C10.1518 11.8866 10.0903 11.8971 10.0391 11.8762L9.99219 11.845C9.95063 11.8033 9.9409 11.7419 9.96191 11.6907Z" fill="#0C0B0B" stroke="#0C0B0B" stroke-width="0.625" />
                            </svg></span>
                        </div>
                        {
                            item.answer && (
                                <div className="faq-answer text-subheading" style={{ maxHeight: openIndex === idx ? '200px' : '0', opacity: openIndex === idx ? 1 : 0, transition: 'all 0.3s cubic-bezier(.4,0,.2,1)' }}>
                                    {item.answer}
                                </div>
                            )
                        }
                    </div>
                ))}
            </div>
        </section >
    );
}

const Pricing = () => {
    const navigate = useNavigate();
    const scheduleBannerRef = useRef(null);
    const scheduleBannerContentRef = useRef(null);
    const scheduleBannerImageRef = useRef(null);
    const atsHeroHeadingRef = useRef(null);
    const atsHeroSubheadingRef = useRef(null);
    const atsHeroButtonRef = useRef(null);
    const pricingTableSectionRef = useRef(null);
    const pricingTableTitleRef = useRef(null);
    const pricingTableRef = useRef(null);
    const ataSectionRef = useRef(null);
    const ataHeadingRef = useRef(null);
    const ataCardsWrapperRef = useRef(null);
    const faqTitleRef = useRef(null);
    const faqListRef = useRef(null);
    const faqAnimatedRef = useRef(false);

    useEffect(() => {
        if (!faqAnimatedRef.current && faqListRef.current) {
            const faqItems = faqListRef.current.querySelectorAll('.faq-item');
            const faqlistitems = [faqTitleRef.current, ...faqItems];
            gsap.fromTo(faqlistitems, {
                y: 40, opacity: 0
            }, {
                y: 0, opacity: 1,
                duration: 0.8,
                stagger: 0.15,
                ease: 'power1.out',
                scrollTrigger: {
                    trigger: faqListRef.current,
                    start: 'top 85%',
                    toggleActions: 'play none none none',
                    once: true,
                    fastScrollEnd: true
                }
            });
            faqAnimatedRef.current = true; // Mark as animated
        }
        // Schedule Demo Banner Animation
        if (scheduleBannerRef.current) {
            const contentEls = scheduleBannerContentRef.current
                ? Array.from(scheduleBannerContentRef.current.children)
                : [];
            const imageEl = scheduleBannerImageRef.current;
            gsap.fromTo(
                [...contentEls, imageEl],
                { y: 40, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.15,
                    ease: 'power1.out',
                    scrollTrigger: {
                        trigger: scheduleBannerRef.current,
                        start: 'top 85%',
                        toggleActions: 'play none none none',
                        once: true,
                        fastScrollEnd: true
                    }
                }
            );
        }

        // Animate ATS Hero Section
        const heroElements = [
            atsHeroHeadingRef.current,
            atsHeroSubheadingRef.current,
            atsHeroButtonRef.current
        ];
        gsap.fromTo(heroElements,
            { y: 50, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: 0.8,
                stagger: 0.2,
                ease: 'power1.out',
                scrollTrigger: {
                    trigger: atsHeroHeadingRef.current,
                    start: 'top 85%',
                    toggleActions: 'play none none none',
                    once: true,
                    fastScrollEnd: true
                }
            }
        );

        // Animate Pricing Table Section
        if (!pricingTableSectionRef.current) return;
        const buttons = pricingTableSectionRef.current.querySelectorAll('.btn--primary.btn--small');
        const elements = [
            pricingTableTitleRef.current,
            pricingTableRef.current,
            ...buttons
        ];
        gsap.fromTo(
            elements,
            { y: 50, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: 0.8,
                stagger: 0.15,
                ease: 'power1.out',
                scrollTrigger: {
                    trigger: pricingTableSectionRef.current,
                    start: 'top 85%',
                    toggleActions: 'play none none none',
                    once: true,
                    fastScrollEnd: true
                }
            }
        );

        // Animate ATA Section
        if (ataSectionRef.current) {
            const headingEl = ataHeadingRef.current;
            const cards = ataCardsWrapperRef.current?.querySelectorAll('.card') || [];
            const ataElements = [headingEl, ...cards];
            gsap.fromTo(
                ataElements,
                { y: 40, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.15,
                    ease: 'power1.out',
                    scrollTrigger: {
                        trigger: ataSectionRef.current,
                        start: 'top 85%',
                        toggleActions: 'play none none none',
                        once: true,
                        fastScrollEnd: true
                    }
                }
            );
        }
    }, []);

    return (
        <>
            {/* Modules Hero Section (keep this at the top) */}
            <section className="ats-hero-section pricing-hero-section">
                <div className="container">
                    <div className="ats-hero-content">
                        <h1
                            ref={atsHeroHeadingRef}
                            className="ats-hero-title text-display-1"
                            style={{ opacity: 0, transform: 'translateY(50px)' }}
                        >
                            "We just wanted HR that
                            actually made sense." - Every team, ever.
                        </h1>
                        <p ref={atsHeroSubheadingRef} className="ats-hero-subheading text-heading-2 fw-500">
                            You grow. Your HR system should, too. <br />
                            With Symplora, you get modular tools, tailored plans, and zero fluff.
                        </p>
                    </div>
                    <div className="button-wrapper">
                        <button ref={atsHeroButtonRef} className="btn btn--primary btn--regular" style={{ opacity: 0, transform: 'translateY(50px)' }}>Book a free demo</button>
                    </div>
                </div>

                {/* pricing table */}
                <div className="container">
                    <section className="pricing-table-section" ref={pricingTableSectionRef}>
                        <h2 className="pricing-table-title" ref={pricingTableTitleRef}>
                            Choose a Plan That Fits Where <span className="highlight">You're Headed</span>
                        </h2>
                        <div className="pricing-table-wrapper">
                            <table className="pricing-table" ref={pricingTableRef}>
                                <thead>
                                    <tr>
                                        <th>Modules</th>
                                        <th>Start up Lite</th>
                                        <th>Start up Pro</th>
                                        <th>Enterprise</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td>Applicant Tracking System (ATS)</td><td>✓</td><td>✓</td><td>✓</td></tr>
                                    <tr><td>Core HR</td><td>✓</td><td>✓</td><td>✓</td></tr>
                                    <tr><td>Leave & Attendance</td><td>✓</td><td>✓</td><td>✓</td></tr>
                                    <tr><td>Payroll</td><td>✓</td><td>✓</td><td>✓</td></tr>
                                    <tr><td>ESOP Management</td><td className="x">x</td><td>✓</td><td>✓</td></tr>
                                    <tr><td>Performance Management System</td><td className="x">x</td><td>✓</td><td>✓</td></tr>
                                    <tr><td>Compensation Management</td><td className="x">x</td><td className="x">x</td><td>✓</td></tr>
                                    <tr><td>Learning Management System (LMS)</td><td className="x">x</td><td className="x">x</td><td>✓</td></tr>
                                    <tr><td>Compliance Management</td><td className="x">x</td><td className="x">x</td><td>✓</td></tr>
                                    <tr><td></td><td><button className="btn btn--primary btn--small">Contact Sales</button></td><td><button className="btn btn--primary btn--small">Contact Sales</button></td><td><button className="btn btn--primary btn--small">Contact Sales</button></td></tr>
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>

                {/* schedule demo banner */}
                <div className="container">
                    <section className="schedule-demo-banner" ref={scheduleBannerRef}>
                        <div className="schedule-demo-banner-content" ref={scheduleBannerContentRef}>
                            <h2 className="text-display-1 fw-700 mb-3">Not Sure What Plan Fits Your Team?</h2>
                            <p className="text-heading-1 text-white mb-4">
                                Let's make it easy. Book a quick call and our experts help you map the right modules for your stage and scale.
                            </p>
                            <ul>
                                <li>✓ Zero pressure</li>
                                <li>✓ Just real answers</li>
                            </ul>
                            <button className="btn btn--white btn--regular mt-4">Schedule a free demo</button>
                        </div>
                        <div className="banner-image">
                            <img src={dashboardDemo} alt="schedule-demo-banner" />
                        </div>
                    </section>
                </div>
                {/* schedule demo banner */}
            </section>

            {/*------------testimonial-slider-section------------*/}
            <section className="testimonial-slider-section">
                <div className="heading-sec container">
                    <h3 className="text-center fw-700 text-heading heading">What Our Customers Say</h3>
                </div>
                <Swiper
                    slidesPerView={3.5}
                    spaceBetween={30}
                    loop={true}
                    pagination={{
                        clickable: true,
                    }}
                    // navigation={true}
                    modules={[Autoplay, Pagination, Navigation]}
                    autoplay={{
                        delay: 4500,
                        disableOnInteraction: false,
                    }}
                    speed={1000}
                    breakpoints={{

                        240: {
                            slidesPerView: 1,
                            spaceBetween: 20,
                        },
                        // >= 640px (mobile)
                        640: {
                            slidesPerView: 1.5,
                            spaceBetween: 20,
                        },
                        // >= 768px (tablets)
                        768: {
                            slidesPerView: 2.5,
                            spaceBetween: 25,
                        },
                        1400: {
                            slidesPerView: 3.5,
                            spaceBetween: 30,
                        }
                        ,
                        2200: {
                            slidesPerView: 4.5,
                            spaceBetween: 30,
                        }
                    }}
                    className="mySwiper"
                >
                    <SwiperSlide>
                        <p className="content text-body">
                            Cardinal ensures we're making decisions based on data we
                            can trust and gives stakeholders visibility into results without
                            constant back and forth among teams. Cardinal solves that
                            for us and provides a central place for our whole company to
                            see the true impact of our entire product strategy as a whole.
                        </p>
                        <div className="profile-info-wrapper flex space-between align-center">
                            <div className="profile-info flex align-center">
                                <div className="avatar">
                                    <img src={BrianSimmonsImg} alt="Brian Simmons" />
                                </div>
                                <div>
                                    <span className="text-body fw-700 name">Brian Simmons</span>
                                    <p className="text-body-1 text-light-2">VP Product</p>
                                    <p className="text-body-1 text-light-2">TataCliq</p>
                                </div>
                            </div>
                            <div className="company-logo">
                                <img src={rapidoSvg} alt="rapido-logo" />
                            </div>
                        </div>
                    </SwiperSlide>
                    <SwiperSlide>
                        <p className="content text-body">
                            Cardinal is a lifesaver for us! We've been with them
                            throughout 2023 and I can't remember how we were
                            managing customer feedback, our backlog or a product
                            roadmap before! Everyone is now aligned on the product at all
                            times.
                        </p>
                        <div className="profile-info-wrapper flex space-between align-center">
                            <div className="profile-info flex align-center">
                                <div className="avatar">
                                    <img src={DummyProfileImg} alt="Brian Simmons" />
                                </div>
                                <div>
                                    <span className="text-body fw-700 name">Brian Simmons</span>
                                    <p className="text-body-1 text-light-2">VP Product</p>
                                    <p className="text-body-1 text-light-2">TataCliq</p>
                                </div>
                            </div>
                            <div className="company-logo">
                                <svg xmlns="http://www.w3.org/2000/svg" width="131" height="20" viewBox="0 0 131 20" fill="none">
                                    <g clip-path="url(#clip0_1971_688)">
                                        <path d="M4.82049 19.2747V0.750977H10.7431C12.0671 0.750977 13.0414 0.825721 13.6917 0.975209C15.7917 1.45144 17.54 3.07873 17.54 5.5816C17.54 7.15766 16.7895 8.56069 15.3909 9.33589C17.4142 10.0363 18.4397 11.5142 18.4397 13.6412C18.4397 15.418 17.6892 17.0453 16.2906 18.0212C14.8408 19.0484 13.8921 19.2726 10.967 19.2726H4.81836L4.82049 19.2747ZM11.2186 7.78549C12.3933 7.78549 13.018 7.28579 13.018 6.30768C13.018 5.3296 12.4189 4.90676 11.2442 4.90676H9.34458V7.78549H11.2186ZM11.8923 15.292C13.2845 15.292 14.1501 14.6876 14.1501 13.4233C14.1501 12.2915 13.389 11.7405 11.9179 11.7405H9.34458V15.292H11.8923Z" fill="#13293E" />
                                        <path d="M28.2618 19.2747V11.6402C28.2618 9.83773 27.5625 8.81054 26.0381 8.81054C24.5138 8.81054 23.7143 9.73738 23.7143 11.4629V19.2726H19.5654V0.750977H23.7143V6.70914C24.3134 5.58373 25.6629 4.90676 27.3131 4.90676C30.4876 4.90676 32.4107 6.90989 32.4107 10.5382V19.2747H28.2618Z" fill="#13293E" />
                                        <path d="M44.5284 19.2747V17.7734H44.4794C43.8803 18.9992 42.2302 19.751 40.4052 19.751C36.3075 19.751 33.457 16.522 33.457 12.3171C33.457 8.11218 36.4311 4.90674 40.4052 4.90674C42.1044 4.90674 43.6799 5.58371 44.4794 6.85865H44.5284V5.38083H48.6773V19.2747H44.5284ZM44.5284 12.315C44.5284 10.3631 42.955 8.78489 41.0042 8.78489C39.0535 8.78489 37.6059 10.3609 37.6059 12.3641C37.6059 14.3672 39.1302 15.8685 41.0554 15.8685C42.9806 15.8685 44.5284 14.316 44.5284 12.3128V12.315Z" fill="#13293E" />
                                        <path d="M50.4279 19.2745V5.38278H54.3273V6.86058H54.3763C55.1012 5.38278 56.2504 4.90869 57.8494 4.90869V9.09011C55.3763 9.1157 54.5746 10.0404 54.5746 11.6186V19.2788H50.4258L50.4279 19.2745Z" fill="#13293E" />
                                        <path d="M68.9209 19.2747V17.7734H68.8723C68.273 18.9992 66.6228 19.751 64.7977 19.751C60.6999 19.751 57.8496 16.522 57.8496 12.3171C57.8496 8.11218 60.8238 4.90674 64.7977 4.90674C66.4971 4.90674 68.0727 5.58371 68.8723 6.85865H68.9209V5.38083H73.0696V19.2747H68.9209ZM68.9209 12.315C68.9209 10.3631 67.3477 8.78489 65.3969 8.78489C63.4462 8.78489 61.9986 10.3609 61.9986 12.3641C61.9986 14.3672 63.5226 15.8685 65.4479 15.8685C67.3732 15.8685 68.9209 14.316 68.9209 12.3128V12.315Z" fill="#13293E" />
                                        <path d="M82.3678 5.38085V8.53504H80.3678V19.2747H76.2209V8.53504H74.1699V5.38085H76.2209V0.750977H80.3678V5.38085H82.3678Z" fill="#13293E" />
                                        <path d="M103.274 19.2746V17.7968H103.225C102.626 19.0738 101.526 19.7252 99.6514 19.7252C96.7285 19.7252 94.3047 17.9719 94.3047 14.0168V5.38281H98.4534V13.1669C98.4534 14.9949 99.1037 15.8449 100.677 15.8449C102.251 15.8449 103.052 14.7942 103.052 13.0921V5.38281H107.201V19.2767H103.278L103.274 19.2746Z" fill="#13293E" />
                                        <path d="M108.921 19.2745V5.38278H112.82V6.86058H112.87C113.595 5.38278 114.744 4.90869 116.343 4.90869V9.09011C113.87 9.1157 113.068 10.0404 113.068 11.6186V19.2788H108.919L108.921 19.2745Z" fill="#13293E" />
                                        <path d="M130.774 10.3782L127.393 13.195C127.333 14.0193 127.022 14.6643 126.316 15.2622C125.02 16.3642 123.331 16.4197 122.139 15.3626L130.069 8.62707C129.883 8.29177 129.691 8.02696 129.433 7.7216C126.653 4.43923 122.114 3.99503 118.95 6.68156C115.843 9.3211 115.637 14.03 118.223 17.0838C120.89 20.2337 125.529 20.5263 128.578 17.9338C130.691 16.1399 131.501 13.3616 130.774 10.3782ZM121.214 9.35524C122.434 8.31737 124 8.23412 125.171 9.1118L120.368 13.1928C119.662 11.856 120.072 10.327 121.214 9.35524Z" fill="#13293E" />
                                        <path d="M87.5761 10.2287H86.8938C88.5375 8.61213 90.1818 6.99553 91.8255 5.37891C90.2393 5.38744 88.6531 5.39813 87.0687 5.40666L82.1055 9.65642V10.1839L87.7509 15.1363H88.4332C88.2886 15.2622 88.1392 15.3861 87.994 15.5121C87.6638 15.7983 87.3289 16.0844 86.9922 16.3706C86.4208 16.8511 85.8493 17.3359 85.2803 17.8227C84.7089 18.3097 84.1375 18.7944 83.5684 19.2749H88.4931L93.4562 15.0252V14.4998L87.5761 10.2287Z" fill="#08A6DF" />
                                        <path d="M4.81999 0.750977H0.295898V4.90676H4.81999V0.750977Z" fill="#F2663F" />
                                        <path d="M4.81999 7.78564H0.295898V11.7407H4.81999V7.78564Z" fill="#78C477" />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_1971_688">
                                            <rect width="131" height="19" fill="white" transform="translate(0 0.750977)" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </div>
                        </div>
                    </SwiperSlide>
                    <SwiperSlide>
                        <p className="content text-body">
                            Holding a product strategy at scale is insanely complicated.
                            Cardinal offers us something we couldn't find elsewhere
                            (including Productboard).  we're handling thousands of
                            feedback items, features, and initiatives across many teams!
                        </p>
                        <div className="profile-info-wrapper flex space-between align-center">
                            <div className="profile-info flex align-center">
                                <div className="avatar">
                                    <img src={DummyProfileImg1} alt="Brian Simmons" />
                                </div>
                                <div>
                                    <span className="text-body fw-700 name">Brian Simmons</span>
                                    <p className="text-body-1 text-light-2">VP Product</p>
                                    <p className="text-body-1 text-light-2">TataCliq</p>
                                </div>
                            </div>
                            <div className="company-logo">
                                <svg xmlns="http://www.w3.org/2000/svg" width="79" height="24" viewBox="0 0 79 24" fill="none">
                                    <g clip-path="url(#clip0_1971_720)">
                                        <path d="M19.2736 21.8114H12.0693L10.9464 18.5818H8.32673L7.20387 21.8114H0L9.6368 0.894043L19.2736 21.8114ZM10.6424 13.8865C10.6424 13.6055 10.5409 13.3569 10.3384 13.1417C10.1354 12.926 9.9014 12.8182 9.6368 12.8182C9.37171 12.8182 9.13772 12.926 8.93519 13.1417C8.73217 13.3569 8.63068 13.6055 8.63068 13.8865C8.63068 14.1681 8.73217 14.4167 8.93519 14.6319C9.13772 14.8475 9.37171 14.9549 9.6368 14.9549C9.9014 14.9549 10.1354 14.8475 10.3384 14.6319C10.5409 14.4167 10.6424 14.1681 10.6424 13.8865ZM32.4421 15.4521L31.039 21.8114H19.9053V2.93134H26.922V15.4521H32.4421ZM46.4759 15.4521L45.0727 21.8114H33.939V2.93134H40.9562V15.4521H46.4759ZM61.6798 15.4521L60.0893 21.8114H47.9733V2.93134H60.0893L61.6798 9.29106H54.9904C54.7095 9.29106 54.5849 9.56944 54.6158 9.83452C54.6475 10.0662 54.7716 10.3053 54.9904 10.3053H58.9666V14.4379H54.9904C54.741 14.4379 54.6158 14.6948 54.6158 14.9434C54.6158 15.1919 54.741 15.4521 54.9904 15.4521H61.6798ZM78.8949 23.7494L70.6619 16.843V21.8114H63.5979V1.78863L71.8784 7.89975V2.9314H78.895L78.8949 23.7494Z" fill="#233A6C" />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_1971_720">
                                            <rect width="79" height="22.8554" fill="white" transform="translate(0 0.894043)" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </div>
                        </div>
                    </SwiperSlide>
                    <SwiperSlide>
                        <p className="content text-body">
                            Cardinal ensures we're making decisions based on data we
                            can trust and gives stakeholders visibility into results without
                            constant back and forth among teams. Cardinal solves that
                            for us and provides a central place for our whole company to
                            see the true impact of our entire product strategy as a whole.
                        </p>
                        <div className="profile-info-wrapper flex space-between align-center">
                            <div className="profile-info flex align-center">
                                <div className="avatar">
                                    <img src={BrianSimmonsImg} alt="Brian Simmons" />
                                </div>
                                <div>
                                    <span className="text-body fw-700 name">Brian Simmons</span>
                                    <p className="text-body-1 text-light-2">VP Product</p>
                                    <p className="text-body-1 text-light-2">TataCliq</p>
                                </div>
                            </div>
                            <div className="company-logo">
                                <svg xmlns="http://www.w3.org/2000/svg" width="76" height="49" viewBox="0 0 76 49" fill="none">
                                    <path d="M44.1064 13.3984H49.6824V15.151H48.03V19.4441H45.7665V15.151H44.1071V13.3984H44.1064Z" fill="black" />
                                    <path d="M52.8773 15.7871L51.5823 19.4435H49.2764L51.5982 13.3984H54.1545L56.4794 19.4435H54.1862L52.8773 15.7871Z" fill="black" />
                                    <path d="M40.9036 15.7871L39.6087 19.4435H37.3027L39.6245 13.3984H42.1808L44.5058 19.4435H42.2125L40.9036 15.7871Z" fill="black" />
                                    <path d="M10.9536 13.5562H24.7848C27.0559 13.5562 28.8966 15.4095 28.8966 17.6948V31.6166C28.8966 33.9025 27.0553 35.7553 24.7848 35.7553H10.9536C8.6825 35.7553 6.8418 33.9019 6.8418 31.6166V17.6948C6.8418 15.4089 8.68313 13.5562 10.9536 13.5562Z" fill="#AB1E40" />
                                    <path d="M67.571 29.1382C67.6046 26.8344 65.7239 24.8885 63.4218 24.8445C61.0651 24.7992 59.0622 26.7432 59.002 29.1331C58.9462 31.3706 60.9365 33.4013 63.2171 33.432C65.639 33.4651 67.5361 31.5939 67.571 29.1382ZM68.4704 32.889C68.9445 33.356 69.4186 33.8262 69.8978 34.2913C70.2091 34.5944 70.2959 34.9606 70.1729 35.3651C70.0449 35.7894 69.7349 36.0299 69.302 36.0931C68.9445 36.1454 68.6504 35.9954 68.4019 35.7498C68.0052 35.3568 67.6058 34.967 67.2243 34.5599C67.0677 34.3922 66.9644 34.3647 66.7546 34.4981C65.6574 35.1954 64.4531 35.5297 63.1518 35.5157C60.1682 35.4831 57.534 33.1659 57.0358 30.1967C56.4292 26.5818 59.0603 23.3905 62.3119 22.861C65.7353 22.3027 69.0136 24.648 69.546 28.0868C69.7983 29.7175 69.5029 31.2443 68.6092 32.6421C68.568 32.7072 68.5332 32.7774 68.4704 32.889Z" fill="black" />
                                    <path d="M34.2209 29.2328C34.2526 30.0692 34.3312 30.889 34.591 31.6853C34.9935 32.9204 35.8727 33.561 37.1226 33.6886C37.6272 33.7402 38.1469 33.7166 38.6534 33.6656C39.4869 33.5807 40.1315 33.1176 40.7292 32.5523C40.9403 32.352 41.2452 32.1835 41.5273 32.1427C41.9 32.0891 42.2023 32.3181 42.3766 32.6735C42.5592 33.0461 42.499 33.3868 42.2442 33.7007C41.5659 34.5377 40.6811 35.0475 39.6606 35.3301C38.1958 35.7365 36.736 35.7046 35.3016 35.1987C34.0187 34.7457 33.1205 33.8493 32.6686 32.5727C31.8902 30.3716 31.863 28.1354 32.666 25.9388C33.369 24.0165 34.8636 23.1016 36.8323 22.8694C37.8871 22.745 38.919 22.8113 39.9268 23.1469C40.78 23.4308 41.5013 23.9183 42.0527 24.6405C42.2606 24.9136 42.4134 25.2121 42.3304 25.5726C42.1617 26.3069 41.2889 26.5628 40.7248 26.0441C40.4548 25.7953 40.2044 25.5184 39.9097 25.3034C38.8277 24.5135 36.961 24.4574 35.8252 25.1662C34.953 25.7104 34.5815 26.5736 34.4085 27.5351C34.3084 28.0946 34.2817 28.6669 34.2209 29.2328Z" fill="black" />
                                    <path d="M46.2926 33.4108H46.7369C48.0281 33.4108 49.3192 33.4064 50.6104 33.4121C51.1003 33.414 51.4299 33.6437 51.5656 34.0488C51.6987 34.4418 51.6043 34.8572 51.2753 35.1073C51.1263 35.2215 50.9165 35.314 50.734 35.3159C48.8755 35.3325 47.0171 35.3395 45.1586 35.3255C44.5837 35.321 44.2237 34.9057 44.2231 34.3181C44.2199 30.8786 44.2199 27.4392 44.2231 24.0004C44.2231 23.3751 44.6668 22.9285 45.2626 22.9336C45.859 22.9387 46.2907 23.3873 46.2907 24.0163C46.2932 26.997 46.292 29.9765 46.292 32.9566L46.2926 33.4108Z" fill="black" />
                                    <path d="M55.225 30.8438C55.225 31.9532 55.2301 33.0627 55.2231 34.1728C55.218 34.9531 54.7135 35.4176 53.9978 35.3187C53.6188 35.2663 53.2632 34.9486 53.1834 34.5748C53.1529 34.4344 53.1377 34.2864 53.1377 34.1416C53.1352 31.9679 53.1358 29.7936 53.1358 27.6193C53.1358 27.1887 53.2157 26.7976 53.6137 26.5417C54.2565 26.1296 55.0767 26.4913 55.199 27.2474C55.2205 27.3794 55.2269 27.5153 55.2269 27.6493C55.2294 28.7141 55.2281 29.7789 55.2281 30.8431H55.225V30.8438Z" fill="black" />
                                    <path d="M55.533 23.4262C55.9399 23.8358 55.9399 24.5019 55.533 24.9108C55.1261 25.3211 54.4643 25.3211 54.058 24.9108L52.7789 23.624C52.372 23.2144 52.372 22.549 52.7783 22.1387C53.1858 21.7291 53.8469 21.7291 54.2532 22.1387L55.533 23.4262Z" fill="#AB1E40" />
                                    <path d="M22.7518 24.7297C22.7879 22.2294 20.7475 20.1182 18.2495 20.0704C15.6926 20.0212 13.5185 22.1305 13.4538 24.7239C13.393 27.1521 15.5531 29.3558 18.0277 29.389C20.655 29.4247 22.7137 27.3946 22.7518 24.7297ZM23.7279 28.7994C24.2426 29.306 24.7566 29.8164 25.277 30.3217C25.6149 30.6509 25.7087 31.0477 25.5756 31.4873C25.4361 31.9473 25.1002 32.2089 24.6305 32.2772C24.2426 32.334 23.9231 32.1713 23.6537 31.9052C23.2234 31.479 22.7898 31.056 22.3759 30.6139C22.206 30.4321 22.0945 30.4021 21.8663 30.5469C20.6753 31.3036 19.3689 31.666 17.9567 31.6513C14.719 31.6162 11.8603 29.1012 11.3197 25.88C10.6611 21.9576 13.5166 18.4945 17.0446 17.9197C20.7596 17.3142 24.3167 19.8586 24.8942 23.5902C25.168 25.36 24.8473 27.0169 23.8775 28.5334C23.8331 28.6029 23.7957 28.6782 23.7279 28.7994Z" fill="white" />
                                    <path d="M32.1465 13.3984H37.7225V15.151H36.07V19.4441H33.8059V15.151H32.1465V13.3984Z" fill="black" />
                                </svg>
                            </div>
                        </div>
                    </SwiperSlide>
                    <SwiperSlide>
                        <p className="content text-body">
                            Cardinal ensures we're making decisions based on data we
                            can trust and gives stakeholders visibility into results without
                            constant back and forth among teams. Cardinal solves that
                            for us and provides a central place for our whole company to
                            see the true impact of our entire product strategy as a whole.
                        </p>
                        <div className="profile-info-wrapper flex space-between align-center">
                            <div className="profile-info flex align-center">
                                <div className="avatar">
                                    <img src={BrianSimmonsImg} alt="Brian Simmons" />
                                </div>
                                <div>
                                    <span className="text-body fw-700 name">Brian Simmons</span>
                                    <p className="text-body-1 text-light-2">VP Product</p>
                                    <p className="text-body-1 text-light-2">TataCliq</p>
                                </div>
                            </div>
                            <div className="company-logo">
                                <svg xmlns="http://www.w3.org/2000/svg" width="76" height="49" viewBox="0 0 76 49" fill="none">
                                    <path d="M44.1064 13.3984H49.6824V15.151H48.03V19.4441H45.7665V15.151H44.1071V13.3984H44.1064Z" fill="black" />
                                    <path d="M52.8773 15.7871L51.5823 19.4435H49.2764L51.5982 13.3984H54.1545L56.4794 19.4435H54.1862L52.8773 15.7871Z" fill="black" />
                                    <path d="M40.9036 15.7871L39.6087 19.4435H37.3027L39.6245 13.3984H42.1808L44.5058 19.4435H42.2125L40.9036 15.7871Z" fill="black" />
                                    <path d="M10.9536 13.5562H24.7848C27.0559 13.5562 28.8966 15.4095 28.8966 17.6948V31.6166C28.8966 33.9025 27.0553 35.7553 24.7848 35.7553H10.9536C8.6825 35.7553 6.8418 33.9019 6.8418 31.6166V17.6948C6.8418 15.4089 8.68313 13.5562 10.9536 13.5562Z" fill="#AB1E40" />
                                    <path d="M67.571 29.1382C67.6046 26.8344 65.7239 24.8885 63.4218 24.8445C61.0651 24.7992 59.0622 26.7432 59.002 29.1331C58.9462 31.3706 60.9365 33.4013 63.2171 33.432C65.639 33.4651 67.5361 31.5939 67.571 29.1382ZM68.4704 32.889C68.9445 33.356 69.4186 33.8262 69.8978 34.2913C70.2091 34.5944 70.2959 34.9606 70.1729 35.3651C70.0449 35.7894 69.7349 36.0299 69.302 36.0931C68.9445 36.1454 68.6504 35.9954 68.4019 35.7498C68.0052 35.3568 67.6058 34.967 67.2243 34.5599C67.0677 34.3922 66.9644 34.3647 66.7546 34.4981C65.6574 35.1954 64.4531 35.5297 63.1518 35.5157C60.1682 35.4831 57.534 33.1659 57.0358 30.1967C56.4292 26.5818 59.0603 23.3905 62.3119 22.861C65.7353 22.3027 69.0136 24.648 69.546 28.0868C69.7983 29.7175 69.5029 31.2443 68.6092 32.6421C68.568 32.7072 68.5332 32.7774 68.4704 32.889Z" fill="black" />
                                    <path d="M34.2209 29.2328C34.2526 30.0692 34.3312 30.889 34.591 31.6853C34.9935 32.9204 35.8727 33.561 37.1226 33.6886C37.6272 33.7402 38.1469 33.7166 38.6534 33.6656C39.4869 33.5807 40.1315 33.1176 40.7292 32.5523C40.9403 32.352 41.2452 32.1835 41.5273 32.1427C41.9 32.0891 42.2023 32.3181 42.3766 32.6735C42.5592 33.0461 42.499 33.3868 42.2442 33.7007C41.5659 34.5377 40.6811 35.0475 39.6606 35.3301C38.1958 35.7365 36.736 35.7046 35.3016 35.1987C34.0187 34.7457 33.1205 33.8493 32.6686 32.5727C31.8902 30.3716 31.863 28.1354 32.666 25.9388C33.369 24.0165 34.8636 23.1016 36.8323 22.8694C37.8871 22.745 38.919 22.8113 39.9268 23.1469C40.78 23.4308 41.5013 23.9183 42.0527 24.6405C42.2606 24.9136 42.4134 25.2121 42.3304 25.5726C42.1617 26.3069 41.2889 26.5628 40.7248 26.0441C40.4548 25.7953 40.2044 25.5184 39.9097 25.3034C38.8277 24.5135 36.961 24.4574 35.8252 25.1662C34.953 25.7104 34.5815 26.5736 34.4085 27.5351C34.3084 28.0946 34.2817 28.6669 34.2209 29.2328Z" fill="black" />
                                    <path d="M46.2926 33.4108H46.7369C48.0281 33.4108 49.3192 33.4064 50.6104 33.4121C51.1003 33.414 51.4299 33.6437 51.5656 34.0488C51.6987 34.4418 51.6043 34.8572 51.2753 35.1073C51.1263 35.2215 50.9165 35.314 50.734 35.3159C48.8755 35.3325 47.0171 35.3395 45.1586 35.3255C44.5837 35.321 44.2237 34.9057 44.2231 34.3181C44.2199 30.8786 44.2199 27.4392 44.2231 24.0004C44.2231 23.3751 44.6668 22.9285 45.2626 22.9336C45.859 22.9387 46.2907 23.3873 46.2907 24.0163C46.2932 26.997 46.292 29.9765 46.292 32.9566L46.2926 33.4108Z" fill="black" />
                                    <path d="M55.225 30.8438C55.225 31.9532 55.2301 33.0627 55.2231 34.1728C55.218 34.9531 54.7135 35.4176 53.9978 35.3187C53.6188 35.2663 53.2632 34.9486 53.1834 34.5748C53.1529 34.4344 53.1377 34.2864 53.1377 34.1416C53.1352 31.9679 53.1358 29.7936 53.1358 27.6193C53.1358 27.1887 53.2157 26.7976 53.6137 26.5417C54.2565 26.1296 55.0767 26.4913 55.199 27.2474C55.2205 27.3794 55.2269 27.5153 55.2269 27.6493C55.2294 28.7141 55.2281 29.7789 55.2281 30.8431H55.225V30.8438Z" fill="black" />
                                    <path d="M55.533 23.4262C55.9399 23.8358 55.9399 24.5019 55.533 24.9108C55.1261 25.3211 54.4643 25.3211 54.058 24.9108L52.7789 23.624C52.372 23.2144 52.372 22.549 52.7783 22.1387C53.1858 21.7291 53.8469 21.7291 54.2532 22.1387L55.533 23.4262Z" fill="#AB1E40" />
                                    <path d="M22.7518 24.7297C22.7879 22.2294 20.7475 20.1182 18.2495 20.0704C15.6926 20.0212 13.5185 22.1305 13.4538 24.7239C13.393 27.1521 15.5531 29.3558 18.0277 29.389C20.655 29.4247 22.7137 27.3946 22.7518 24.7297ZM23.7279 28.7994C24.2426 29.306 24.7566 29.8164 25.277 30.3217C25.6149 30.6509 25.7087 31.0477 25.5756 31.4873C25.4361 31.9473 25.1002 32.2089 24.6305 32.2772C24.2426 32.334 23.9231 32.1713 23.6537 31.9052C23.2234 31.479 22.7898 31.056 22.3759 30.6139C22.206 30.4321 22.0945 30.4021 21.8663 30.5469C20.6753 31.3036 19.3689 31.666 17.9567 31.6513C14.719 31.6162 11.8603 29.1012 11.3197 25.88C10.6611 21.9576 13.5166 18.4945 17.0446 17.9197C20.7596 17.3142 24.3167 19.8586 24.8942 23.5902C25.168 25.36 24.8473 27.0169 23.8775 28.5334C23.8331 28.6029 23.7957 28.6782 23.7279 28.7994Z" fill="white" />
                                    <path d="M32.1465 13.3984H37.7225V15.151H36.07V19.4441H33.8059V15.151H32.1465V13.3984Z" fill="black" />
                                </svg>
                            </div>
                        </div>
                    </SwiperSlide>
                    <SwiperSlide>
                        <p className="content text-body">
                            Cardinal ensures we're making decisions based on data we
                            can trust and gives stakeholders visibility into results without
                            constant back and forth among teams. Cardinal solves that
                            for us and provides a central place for our whole company to
                            see the true impact of our entire product strategy as a whole.
                        </p>
                        <div className="profile-info-wrapper flex space-between align-center">
                            <div className="profile-info flex align-center">
                                <div className="avatar">
                                    <img src={BrianSimmonsImg} alt="Brian Simmons" />
                                </div>
                                <div>
                                    <span className="text-body fw-700 name">Brian Simmons</span>
                                    <p className="text-body-1 text-light-2">VP Product</p>
                                    <p className="text-body-1 text-light-2">TataCliq</p>
                                </div>
                            </div>
                            <div className="company-logo">
                                <img src={rapidoSvg} alt="rapido-logo" />
                            </div>
                        </div>
                    </SwiperSlide>
                </Swiper>
            </section>
            {/*------------testimonial-slider-section------------*/}

            {/*------------ATA-section------------*/}
            <section className="ata-section pricing-ata-section" ref={ataSectionRef}>
                <div className="container">
                    <h3 className="text-center fw-700 text-heading heading" ref={ataHeadingRef}>Why Fast-Growing Teams Choose Symplora</h3>
                    <div className="ats-card-wrapper flex justify-center" ref={ataCardsWrapperRef}>
                        <div className="card flex" >
                            <img src={aiPowered} />
                            <span className="text-body fw-700">AI-Powered Insights</span>
                            <p className="text-body-1">Automate CV parsing and job description generation tailored to roles.</p>
                        </div>
                        <div className="card flex" >
                            <img src={click} />
                            <span className="text-body fw-700">One-Click Efficiency</span>
                            <p className="text-body-1">Post job openings to multiple platforms instantly.</p>
                        </div>
                        <div className="card flex">
                            <img src={calender} />
                            <span className="text-body fw-700">Seamless Scheduling</span>
                            <p className="text-body-1">Automate interviews and feedback loops for faster decisions.</p>
                        </div>
                        <div className="card flex">
                            <img src={offer} />
                            <span className="text-body fw-700">Integrated Offers</span>
                            <p className="text-body-1">Simplify the offer approval process with built-in digital signatures.</p>
                        </div>
                        <div className="card flex">
                            <img src={analysis} />
                            <span className="text-body fw-700">Real-Time Analytics</span>
                            <p className="text-body-1">Track hiring progress with actionable insights.</p>
                        </div>
                    </div>
                </div>
            </section>
            {/*------------ATS-section------------*/}

            {/* -----------faq-section ------------*/}
            <FaqSection faqRefs={[faqTitleRef, faqListRef]} />
            {/* -----------faq-section ------------*/}
        </>
    )
}

export default Pricing